import React, { useState, useEffect, useRef, useMemo } from 'react';

const HeroAnimation = ({ onComplete }) => {
  const [phase, setPhase] = useState('cycling'); // 'cycling' | 'settling' | 'done'
  const [currentIndex, setCurrentIndex] = useState(0);
  const [speed, setSpeed] = useState(400);
  const cycleCountRef = useRef(0);
  const timeoutRef = useRef(null);

  // Word pairs that cycle through - marketing/tech related buzz words
  const wordPairs = useMemo(() => [
    ['DIGITAL', 'NOISE.'],
    ['BRAND', 'CHAOS.'],
    ['AI', 'HYPE.'],
    ['DATA', 'DRIVEN.'],
    ['GROWTH', 'HACK.'],
    ['VIRAL', 'CONTENT.'],
    ['FAST', 'SCALE.'],
    ['MORE', 'CLICKS.'],
    ['QUICK', 'WINS.'],
    ['LOUD', 'ADS.'],
    ['BIG', 'DATA.'],
    ['SMART', 'TECH.'],
    ['HUMAN', 'SENSE.'],
  ], []);

  useEffect(() => {
    if (phase !== 'cycling') return;

    const maxCycles = 30;
    
    const runCycle = () => {
      cycleCountRef.current++;
      const count = cycleCountRef.current;
      
      // Speed curve: slow start -> fast middle -> slow end
      let newSpeed;
      if (count < 6) {
        newSpeed = 400 - (count * 50); // 400 -> 150
      } else if (count < 20) {
        newSpeed = 50; // Maximum speed
      } else {
        newSpeed = 50 + ((count - 20) * 40); // 50 -> 450 (slowing down)
      }
      
      setSpeed(newSpeed);
      setCurrentIndex(prev => (prev + 1) % wordPairs.length);

      if (count >= maxCycles) {
        // Transition to settling phase
        setPhase('settling');
        setTimeout(() => {
          setPhase('done');
          onComplete?.();
        }, 600);
      } else {
        timeoutRef.current = setTimeout(runCycle, newSpeed);
      }
    };

    timeoutRef.current = setTimeout(runCycle, speed);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [phase, wordPairs, onComplete]);

  const currentPair = wordPairs[currentIndex];

  if (phase === 'done') {
    // Final settled state - single H1 with spans for styling
    return (
      <div className="relative w-full">
        <h1 className="headline-xl text-center px-2 md:px-0 text-[11vw] md:text-[9vw] lg:text-[8vw] font-bold">
          <span 
            className="block animate-settle"
            style={{ animationDelay: '0s' }}
          >
            Smart Tech,
          </span>
          <span 
            className="block animate-settle"
            style={{ animationDelay: '0.1s' }}
          >
            Human Sense,
          </span>
          <span 
            className="block text-[#F6CD17] animate-settle"
            style={{ animationDelay: '0.2s' }}
          >
            Better Marketing
          </span>
        </h1>
      </div>
    );
  }

  if (phase === 'settling') {
    // Transition state - blur out
    return (
      <div className="relative w-full">
        <h1 className="headline-xl text-center opacity-50 blur-sm transition-all duration-300 px-2 md:px-0">
          <span className="block text-[13vw] md:text-[11vw] lg:text-[9vw] font-bold">
            {currentPair[0]}
          </span>
          <span className="block text-[13vw] md:text-[11vw] lg:text-[9vw] font-bold">
            {currentPair[1]}
          </span>
        </h1>
      </div>
    );
  }

  // Cycling state - two words at a time
  return (
    <div className="relative w-full">
      <h1 className="headline-xl text-center px-2 md:px-0">
        <span 
          key={`word1-${currentIndex}`}
          className="block text-[13vw] md:text-[11vw] lg:text-[9vw] font-bold"
          style={{ animation: 'wordFlip 0.1s ease-out' }}
        >
          {currentPair[0]}
        </span>
        <span 
          key={`word2-${currentIndex}`}
          className="block text-[13vw] md:text-[11vw] lg:text-[9vw] font-bold"
          style={{ animation: 'wordFlip 0.1s ease-out', animationDelay: '0.03s' }}
        >
          {currentPair[1]}
        </span>
      </h1>
    </div>
  );
};

export default HeroAnimation;
